import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { resolveFontSize } from "@/utils/headingSize";
import { buildTextStyle } from "@/components/ui/TextStyleControls";

/**
 * Multi-tier pricing comparison. Fully admin-managed (see
 * PricingTableConfigFields in ModuleConfigFields.jsx) — tiers, features,
 * CTAs and colours all come from `config`, nothing is hardcoded here.
 */
export default function PricingTableModule({ config = {} }) {
  const tiers = Array.isArray(config.tiers) ? config.tiers : [];
  if (tiers.length === 0) return null;

  const cardBg = config.card_bg_color || "var(--card-bg, #ffffff)";
  const highlightBg = config.highlight_bg_color || cardBg;
  const accent = config.highlight_accent_color || "var(--button-bg, #2f4f4f)";
  const ctaBg = config.cta_bg_color || accent;

  // Font/size/colour/justification for every heading-level text in this
  // instance — font choices come from the same standard list as Master
  // Admin → Site Styles (see utils/fonts.js), so admins pick a familiar
  // set rather than a bespoke one per module.
  const headingStyle = {
    ...buildTextStyle(config, "heading_", "heading_font_size"),
    textAlign: config.heading_text_align || "center",
    fontSize: resolveFontSize(config.heading_font_size, "h2"),
  };
  const subheadingStyle = {
    ...buildTextStyle(config, "subheading_", "subheading_font_size"),
    textAlign: config.subheading_text_align || "center",
    fontSize: resolveFontSize(config.subheading_font_size, "h5"),
  };
  const tierNameStyle = {
    ...buildTextStyle(config, "tier_name_", "tier_name_font_size"),
    textAlign: config.tier_name_text_align || "left",
    fontSize: resolveFontSize(config.tier_name_font_size, "h4"),
  };

  const renderCta = (tier, idx) => {
    if (!tier.cta_text) return null;
    const link = tier.cta_link || "#";
    const isInternal = link.startsWith("/");
    const className = "inline-flex items-center justify-center w-full font-semibold text-sm px-4 py-2.5 rounded-md transition-transform hover:-translate-y-0.5";
    const style = { backgroundColor: ctaBg, color: config.cta_text_color || "var(--button-text, #ffffff)" };
    return isInternal ? (
      <Link to={link} className={className} style={style} data-testid={`pricing-table-cta-${idx}`}>{tier.cta_text}</Link>
    ) : (
      <a href={link} target="_blank" rel="noopener noreferrer" className={className} style={style} data-testid={`pricing-table-cta-${idx}`}>{tier.cta_text}</a>
    );
  };

  return (
    <section className="py-16" data-testid="pricing-table-module">
      <div className="w-full">
        {(config.heading || config.subheading) && (
          <div className="mb-10" style={{ textAlign: headingStyle.textAlign }}>
            {config.heading && (
              <h2
                className="font-bold"
                style={headingStyle}
                data-testid="pricing-table-public-heading"
              >
                {config.heading}
              </h2>
            )}
            {config.subheading && (
              <p
                className="mt-3 max-w-2xl"
                style={{
                  ...subheadingStyle,
                  marginLeft: subheadingStyle.textAlign !== "left" ? "auto" : undefined,
                  marginRight: subheadingStyle.textAlign !== "right" ? "auto" : undefined,
                }}
              >
                {config.subheading}
              </p>
            )}
          </div>
        )}
        <div
          className="pricing-table-grid grid gap-6 items-stretch"
          style={{ "--pricing-tier-count": tiers.length }}
        >
          {tiers.map((tier, idx) => {
            const features = (tier.features || []).filter(Boolean);
            return (
              <div
                key={idx}
                className={`relative rounded-xl border flex flex-col p-6 transition-transform ${tier.highlighted ? "shadow-xl md:-translate-y-2 z-10" : "shadow-sm"}`}
                style={{
                  backgroundColor: tier.highlighted ? highlightBg : cardBg,
                  borderColor: tier.highlighted ? accent : "hsl(var(--border))",
                  borderWidth: tier.highlighted ? "2px" : "1px",
                }}
                data-testid={`pricing-table-tier-card-${idx}`}
              >
                {tier.highlighted && tier.badge_text && (
                  <span
                    className="absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-wide px-3 py-1 rounded-full text-white whitespace-nowrap"
                    style={{ backgroundColor: accent }}
                    data-testid={`pricing-table-public-badge-${idx}`}
                  >
                    {tier.badge_text}
                  </span>
                )}
                <h3 className="font-bold" style={tierNameStyle} data-testid={`pricing-table-public-tier-name-${idx}`}>{tier.name}</h3>
                {tier.description && <p className="text-sm text-muted-foreground mt-1">{tier.description}</p>}
                <div className="mt-4 flex items-baseline gap-1" data-testid={`pricing-table-public-tier-price-${idx}`}>
                  <span
                    className="text-3xl font-serif font-bold"
                    style={{
                      color: config.price_color || undefined,
                      fontFamily: config.price_font_family || undefined,
                    }}
                  >
                    {tier.price}
                  </span>
                  {tier.interval && <span className="text-sm text-muted-foreground">{tier.interval}</span>}
                </div>
                {features.length > 0 && (
                  <ul className="mt-5 space-y-2 flex-1">
                    {features.map((f, fi) => (
                      <li key={fi} className="flex items-start gap-2 text-sm">
                        <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className={features.length > 0 ? "mt-auto pt-6" : "mt-6"}>{renderCta(tier, idx)}</div>
                {tier.image_url && (
                  <img
                    src={tier.image_url}
                    alt={tier.name || `Tier ${idx + 1}`}
                    className="w-full aspect-video object-cover rounded-lg mt-5"
                    data-testid={`pricing-table-public-tier-image-${idx}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
